import { type AliasColors, type AliasColorScheme } from "@/styles/themes/colors/alias";
import { Checkbox as ChakraCheckbox, type CheckboxProps as ChakraCheckboxProps, Icon } from "@chakra-ui/react";
import { css } from "@emotion/react";
import { useMemo, type FC } from "react";
import { Typography, type TypographyProps } from "@/components/essentials/typography";
import { injectCheckboxSize } from "./variant";
import { FaCheck } from "react-icons/fa";
import { type CheckboxSize, type CheckboxBreakPointSize } from "./type";
import { checkboxTestId, checkboxTypographyTestId } from "./const";
import { type ColorPalettes } from "@/styles/themes/colors/palettes";

export interface CheckboxProps extends Omit<ChakraCheckboxProps, "colorScheme" | "size"> {
  readonly colorScheme?: AliasColorScheme;
  readonly size?: CheckboxSize | CheckboxBreakPointSize;
  readonly textColor?: AliasColors | ColorPalettes;
}

export const Checkbox: FC<CheckboxProps> = ({
  children,
  colorScheme = "primary",
  size = {
    base: "sm",
    sm: "md",
    md: "md",
    lg: "lg",
    xl: "lg",
    "2xl": "lg",
  },
  textColor = "gray.main",
  ...rest
}) => {
  const { iconSize, typographyVariant } = useMemo(() => injectCheckboxSize(size), [size]);

  return (
    <ChakraCheckbox
      borderRadius="0.125rem"
      colorScheme={colorScheme}
      size={size as ChakraCheckboxProps["size"]}
      icon={<Icon fontSize={iconSize} as={FaCheck} />}
      css={css`
        > span:first-of-type {
          box-shadow: unset;
        }
      `}
      data-testid={checkboxTestId}
      {...rest}
    >
      <Typography
        variant={typographyVariant as TypographyProps["variant"]}
        color={textColor}
        data-testid={checkboxTypographyTestId}
      >
        {children}
      </Typography>
    </ChakraCheckbox>
  );
};
